import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getBlogPosts } from "../../../api/storefront";
import { getImageUrl } from "../../../api/client";
import "./HomeBlogSection.css";

const DEFAULT_LIMIT = 3;

/** Homepage teaser — latest posts; renders nothing when there are no published posts (after load). */
export default function HomeBlogSection({ limit = DEFAULT_LIMIT }) {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const n = Math.min(Math.max(Number(limit) || DEFAULT_LIMIT, 1), 6);
    getBlogPosts({ limit: n })
      .then((rows) => {
        if (!cancelled) setPosts(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [limit]);

  if (!loaded) {
    return (
      <section className="homeBlogSection homeBlogSection--pending" aria-busy="true" aria-labelledby="home-blog-heading">
        <div className="homeBlogSectionInner">
          <header className="homeBlogSectionHeader">
            <h2 id="home-blog-heading">From the blog</h2>
            <p>Rental tips, style notes, and what&apos;s new at Gem Fashion.</p>
          </header>
          <p className="homeBlogSectionStatus">Loading…</p>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return (
      <section className="homeBlogSection homeBlogSection--empty" aria-labelledby="home-blog-heading">
        <div className="homeBlogSectionInner">
          <header className="homeBlogSectionHeader">
            <h2 id="home-blog-heading">From the blog</h2>
            <p>Rental tips, style notes, and what&apos;s new at Gem Fashion.</p>
            <Link className="homeBlogSectionViewAll" to="/blog">
              Visit the blog
            </Link>
          </header>
          <p className="homeBlogSectionEmptyHint">
            New articles will appear here when published in Admin → Blog (check <strong>Published</strong>).
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="homeBlogSection" aria-labelledby="home-blog-heading">
      <div className="homeBlogSectionInner">
        <header className="homeBlogSectionHeader">
          <h2 id="home-blog-heading">From the blog</h2>
          <p>Rental tips, style notes, and what&apos;s new at Gem Fashion.</p>
          <Link className="homeBlogSectionViewAll" to="/blog">
            View all articles
          </Link>
        </header>
        <ul className="homeBlogSectionGrid">
          {posts.map((post) => {
            const dateSrc = post.published_at || post.created_at;
            const dateLabel = dateSrc
              ? new Date(dateSrc).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "";
            const excerpt = (post.excerpt || "").trim();
            return (
              <li key={post.slug} className="homeBlogSectionCard">
                <Link to={`/blog/${encodeURIComponent(post.slug)}`} className="homeBlogSectionCardLink">
                  <div className="homeBlogSectionThumb">
                    {post.featured_image_url ? (
                      <img src={getImageUrl(post.featured_image_url)} alt="" loading="lazy" decoding="async" />
                    ) : (
                      <div className="homeBlogSectionThumbPlaceholder" aria-hidden />
                    )}
                  </div>
                  <div className="homeBlogSectionCardBody">
                    <p className="homeBlogSectionMeta">
                      {post.author_name || "Editor"}
                      {dateLabel ? <span className="homeBlogSectionSep">·</span> : null}
                      {dateLabel}
                      {post.category_label ? (
                        <>
                          <span className="homeBlogSectionSep">·</span>
                          {String(post.category_label).toUpperCase()}
                        </>
                      ) : null}
                    </p>
                    <h3 className="homeBlogSectionTitle">{post.title}</h3>
                    <p className="homeBlogSectionExcerpt">
                      {excerpt || "Read more on the blog."}
                    </p>
                    <span className="homeBlogSectionRead">Read article</span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
